import gsap from 'gsap';

const DEFAULTS = {
    threshold: 0.12,
    rootMargin: '0px 0px -8% 0px',
    stagger: 0.06,
    y: 24,
    duration: 0.65,
    ease: 'power2.out',
    mobileY: 16,
    mobileDuration: 0.5,
};

export function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function isMobileReveal() {
    return window.matchMedia('(max-width: 1024px), (pointer: coarse)').matches;
}

export function getRevealConfig(overrides = {}) {
    const mobile = isMobileReveal();

    return {
        y: mobile ? DEFAULTS.mobileY : DEFAULTS.y,
        duration: mobile ? DEFAULTS.mobileDuration : DEFAULTS.duration,
        ease: DEFAULTS.ease,
        ...overrides,
    };
}

export function waitForLoaderDone() {
    if (!document.body.hasAttribute('data-loader-show')) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        window.addEventListener('site-loader:done', resolve, { once: true });
    });
}

export function revealInstant(nodes) {
    if (!nodes || !nodes.length) return;

    nodes.forEach((node) => {
        gsap.set(node, { clearProps: 'opacity,visibility,transform' });
        node.classList.add('is-revealed');
        node.__revealed = true;
        node.__revealPrepared = true;
    });
}

export function prepareRevealNodes(nodes, config = getRevealConfig()) {
    if (!nodes || !nodes.length || prefersReducedMotion()) return;

    nodes.forEach((node) => {
        if (node.__revealPrepared) return;
        gsap.set(node, { y: config.y, autoAlpha: 0 });
        node.__revealPrepared = true;
    });
}

export function animateRevealNodes(nodes, options = {}) {
    if (!nodes || !nodes.length) return;

    if (prefersReducedMotion()) {
        revealInstant(nodes);
        return;
    }

    const config = getRevealConfig(options);
    const stagger = options.stagger ?? DEFAULTS.stagger;

    gsap.fromTo(
        nodes,
        { y: config.y, autoAlpha: 0 },
        {
            y: 0,
            autoAlpha: 1,
            duration: config.duration,
            ease: config.ease,
            stagger,
            onComplete: () => {
                nodes.forEach((node) => node.classList.add('is-revealed'));
            },
        }
    );
}

export class RevealObserver {
    constructor(options = {}) {
        this.options = { ...DEFAULTS, ...options };
        this.observer = null;
    }

    observe(nodes, callback) {
        const list = Array.from(nodes || []).filter(Boolean);
        if (!list.length) return;

        if (prefersReducedMotion()) {
            revealInstant(list);
            list.forEach((node) => callback?.(node));
            return;
        }

        const config = getRevealConfig(this.options);
        prepareRevealNodes(list, config);
        this._ensureObserver(callback, config);

        list.forEach((node) => {
            if (!node.__revealed) {
                this.observer.observe(node);
            }
        });
    }

    _ensureObserver(callback, config) {
        if (this.observer) return;

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                const node = entry.target;
                this.observer.unobserve(node);

                if (node.__revealed) return;
                node.__revealed = true;

                gsap.to(node, {
                    y: 0,
                    autoAlpha: 1,
                    duration: config.duration,
                    ease: config.ease,
                    onComplete: () => {
                        node.classList.add('is-revealed');
                        callback?.(node);
                    },
                });
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: this.options.rootMargin,
        });
    }

    disconnect() {
        this.observer?.disconnect();
        this.observer = null;
    }
}

export class RevealBlockObserver {
    constructor(options = {}) {
        this.options = { ...DEFAULTS, ...options };
        this.observer = null;
    }

    observe(blocks) {
        const list = Array.from(blocks || []).filter(Boolean);
        if (!list.length) return;

        list.forEach((block) => {
            if (block.__revealBlockWired) return;
            block.__revealBlockWired = true;

            const items = Array.from(block.querySelectorAll('[data-reveal-item]'));
            if (!items.length) return;

            if (prefersReducedMotion()) {
                revealInstant(items);
                return;
            }

            const config = getRevealConfig(this.options);
            prepareRevealNodes(items, config);

            if (!this.observer) {
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;

                        const container = entry.target;
                        this.observer.unobserve(container);

                        if (container.__revealed) return;
                        container.__revealed = true;

                        const stagger = parseFloat(container.getAttribute('data-reveal-stagger')) || this.options.stagger;
                        const nodes = Array.from(container.querySelectorAll('[data-reveal-item]')).filter((node) => !node.__revealed);

                        if (!nodes.length) return;

                        nodes.forEach((node) => {
                            node.__revealed = true;
                        });

                        animateRevealNodes(nodes, { stagger, ...config });
                    });
                }, {
                    threshold: this.options.threshold,
                    rootMargin: this.options.rootMargin,
                });
            }

            this.observer.observe(block);
        });
    }

    disconnect() {
        this.observer?.disconnect();
        this.observer = null;
    }
}
